import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Autocomplete,
  Tooltip,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Popover,
  Snackbar,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { TABLE_CONTAINER_STYLE, NUMERICAL_COLUMN_WIDTH, AMOUNT_COLUMN_WIDTH } from '../../constants/invoiceConstants';
import { useTheme } from '@mui/material/styles';
import { formatNumber, debounce } from '../../utils/invoiceCalculations';
import { useTranslation } from '../../hooks/useTranslation';


const CreditNoteItemsTable = ({
  items,
  onItemChange,
  onRemoveItem,
  visibleColumns,
  products,
  onSettingsClick,
  readOnly = false
}) => {
  // State for settings popover
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const settingsOpen = Boolean(settingsAnchorEl);
  const { t } = useTranslation();

  // State for error message
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  


  // Calculate totals for an item
  const calculateItemTotals = (item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const netValue = qty * price;
    const discountAmount = (netValue * (Number(item.discount) || 0)) / 100;
    const netValueAfterDiscount = netValue - discountAmount;
    const vatRate = Number(item.vatRate) || 0;
    const vatAmount = (netValueAfterDiscount * vatRate) / 100;
    const total = netValueAfterDiscount + vatAmount;

    return {
      netValue,
      discountAmount,
      netValueAfterDiscount,
      vatAmount,
      total
    };
  };
  
  // Calculate totals for the summary row
  const calculateTotals = () => {
    return items.reduce((totals, item) => {
      const itemTotals = calculateItemTotals(item);
      const qty = Number(item.quantity) || 0;
      totals.quantity += qty;
      totals.lineTotal += qty * (Number(item.price) || 0);
      totals.discountAmount += itemTotals.discountAmount || 0;
      totals.netValue += itemTotals.netValueAfterDiscount || 0;
      totals.vatAmount += itemTotals.vatAmount || 0;
      totals.grandTotal += itemTotals.total || 0;
      return totals;
    }, {
      quantity: 0,
      lineTotal: 0,
      discountAmount: 0,
      netValue: 0,
      vatAmount: 0,
      grandTotal: 0
    });
  };

  const totals = calculateTotals();
  
  // Handle settings icon click
  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
    if (onSettingsClick) {
      onSettingsClick(event);
    }
  };

  // Handle settings popover close
  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };
  
  const handleFocus = (event) => {
    event.target.select();
  };

  const handleColumnToggle = (column) => {
    if (onSettingsClick) {
      const event = { target: { name: column, value: !visibleColumns[column] } };
      onSettingsClick(event);
    }
  };

  return (
    <>
      <TableContainer 
        component={Paper} 
        elevation={0} 
        sx={{
          ...TABLE_CONTAINER_STYLE,
          overflow: 'auto',
          maxHeight: '60vh',
          marginBottom: '24px',
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
            padding: '4px 8px',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'text.primary',
            height: '36px',
          },
          '& .MuiTableCell-head': {
            backgroundColor: (theme) => theme.palette.background.default,
            fontWeight: 600,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
        }}
        
      >
        <Table
          size="small"
          sx={{
            width: '100%',
            '& .MuiTableCell-root': {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <TableHead>
          <TableRow>
            <TableCell
              width="100%"
              align="left"
              sx={{
                fontWeight: 600,
                padding: '4px 8px',
              }}
            >
             {t('CreditNotes.Item')}
            </TableCell>
            <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>{t('CreditNotes.Qty')}</TableCell>
            <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>{t('CreditNotes.Unit_Price')}</TableCell>
            {visibleColumns.priceWithVat && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>{t('CreditNotes.Price_VAT')}</TableCell>}
            <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>{t('CreditNotes.Total_Amt')}</TableCell>
            {visibleColumns.discountPerc && <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>{t('CreditNotes.Discount_Perc')} %</TableCell>}
            {visibleColumns.discountAmt && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>{t('CreditNotes.Discount_Amount')}</TableCell>}
            {visibleColumns.netValue && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>{t('CreditNotes.Net_Value')}</TableCell>}
            <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>{t('CreditNotes.VAT_Perc')}</TableCell>
            {visibleColumns.vatAmount && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>{t('CreditNotes.VAT_Amount')}</TableCell>}
            {visibleColumns.grandTotal && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>{t('CreditNotes.Grand_Total')}</TableCell>}
            {visibleColumns.description && <TableCell align="center">{t('CreditNotes.Description')}</TableCell>}
            <TableCell align="center" width="50px">
              <Tooltip title="Column Settings">
                <IconButton 
                  size="small" 
                  onClick={handleSettingsClick}
                  sx={{ p: 0.5 }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const totals = calculateItemTotals(item);
            const hasLimit = item.originalQuantity !== undefined && item.originalQuantity !== null && !isNaN(Number(item.originalQuantity));
            const originalQty = hasLimit ? Number(item.originalQuantity) : null;
            const prevCreditedQty = (item.previouslyCreditedQuantity !== undefined && item.previouslyCreditedQuantity !== null && !isNaN(Number(item.previouslyCreditedQuantity)))
              ? Number(item.previouslyCreditedQuantity)
              : 0;
            const maxAllowedQty = hasLimit ? Math.max(0, originalQty - prevCreditedQty) : null;

            const qtyVal = item.quantity;
            const isEmpty = qtyVal === '' || qtyVal === null || qtyVal === undefined;
            const numQty = Number(qtyVal);
            const isNotPositive = !isEmpty && (isNaN(numQty) || numQty <= 0);
            const isExceeding = !isEmpty && hasLimit && (numQty > maxAllowedQty);
            const isInvalid = isNotPositive || isExceeding;
            const itemName = item.product?.name || item.description || t('CreditNotes.Item') || 'Item';

            const getValidationErrorMessage = (val) => {
              if (val === '' || isNaN(Number(val))) {
                return `Please enter a valid quantity for "${itemName}". Quantity must be greater than 0.`;
              }
              const n = Number(val);
              if (n <= 0) {
                return `Quantity must be greater than 0 for "${itemName}".`;
              }
              if (hasLimit && n > maxAllowedQty) {
                if (prevCreditedQty > 0) {
                  return `Quantity ${n} plus previously credited quantity of ${prevCreditedQty} exceeds original invoice quantity of ${originalQty} for "${itemName}". Maximum permitted quantity is ${maxAllowedQty}.`;
                }
                return `Quantity ${n} exceeds original invoice quantity of ${maxAllowedQty} for "${itemName}". Maximum permitted quantity is ${maxAllowedQty}.`;
              }
              return null;
            };

            return (
              <TableRow key={item.id}>
                <TableCell align="left">
                  <Autocomplete
                    options={products}
                    getOptionLabel={(option) => option.name || ''}
                    value={item.product}
                    onChange={(event, newValue) => onItemChange(item.id, 'product', newValue)}
                    disabled={readOnly}
                    size="small"
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        border: 'none',
                        borderRadius: 0,
                        height: '32px',
                        width: '100%',
                        '& fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiInputBase-input': {
                            color: 'text.primary',
                            fontWeight: 500,
                            padding: '4px 8px !important'
                          }
                        }}
                      />
                    )}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip
                    title={
                      isExceeding
                        ? (prevCreditedQty > 0
                            ? `Max allowed: ${maxAllowedQty} (Original: ${originalQty}, Previously credited: ${prevCreditedQty})`
                            : `Exceeds max permitted quantity (${maxAllowedQty})`)
                        : isNotPositive
                          ? 'Quantity must be greater than 0'
                          : (hasLimit ? (prevCreditedQty > 0 ? `Max: ${maxAllowedQty} (${prevCreditedQty} already credited)` : `Max allowed: ${maxAllowedQty}`) : '')
                    }
                    arrow
                  >
                    <TextField
                      value={item.quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        const newQty = value === '' ? '' : (parseInt(value, 10) || 0);
                        onItemChange(item.id, 'quantity', newQty);

                        if (value !== '') {
                          const errorMsg = getValidationErrorMessage(newQty);
                          if (errorMsg) {
                            setErrorMessage(errorMsg);
                            setShowError(true);
                          } else {
                            setShowError(false);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setErrorMessage(`Quantity cannot be empty for "${itemName}". Quantity must be greater than 0.`);
                          setShowError(true);
                          return;
                        }
                        const currentQty = parseInt(value, 10);
                        const errorMsg = getValidationErrorMessage(currentQty);
                        if (errorMsg) {
                          setErrorMessage(errorMsg);
                          setShowError(true);
                        }
                      }}
                      type="number"
                      size="small"
                      variant="standard"
                      error={isInvalid}
                      InputProps={{ 
                        disableUnderline: true,
                        inputProps: { 
                          min: 1,
                          max: hasLimit ? maxAllowedQty : undefined,
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: NUMERICAL_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                          color: isInvalid ? 'error.main' : 'text.primary',
                          fontWeight: 500
                        },
                        '& .MuiInputBase-root': {
                          border: isInvalid ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: '4px',
                          paddingX: '4px',
                          backgroundColor: isInvalid ? 'rgba(211, 47, 47, 0.04)' : 'transparent',
                          transition: 'border-color 0.2s ease, background-color 0.2s ease',
                        },
                        '& .MuiInputBase-root:hover': {
                          borderColor: isInvalid ? '#d32f2f' : '#1976d2',
                        },
                        '& .MuiInputBase-root.Mui-focused': {
                          borderColor: isInvalid ? '#d32f2f' : '#1976d2',
                          backgroundColor: isInvalid ? 'rgba(211, 47, 47, 0.08)' : '#e3f2fd',
                        },
                      }}
                      onFocus={(e) => e.target.select()}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <TextField
                    value={formatNumber(item.price)}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue === '' || !isNaN(parseFloat(newValue))) {
                        onItemChange(item.id, 'price', parseFloat(newValue) || 0);
                      }
                    }}
                    type="text"
                    size="small"
                    variant="standard"
                    InputProps={{ 
                      disableUnderline: true,
                      inputProps: { 
                        min: 0, 
                        step: 0.01,
                        style: { 
                          textAlign: 'center',
                          color: 'rgba(0, 0, 0, 0.87)',
                          opacity: 1,
                          '-webkit-text-fill-color': 'rgba(0, 0, 0, 0.87)'
                        }
                      }
                    }}
                    sx={{
                      width: AMOUNT_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                        color: 'text.primary',
                        fontWeight: 500
                      },
                      '& .MuiInputBase-root': {
                        border: '1px solid transparent',
                        borderRadius: '4px',
                        paddingX: '4px',
                        transition: 'border-color 0.2s ease',
                      },
                      '& .MuiInputBase-root.Mui-focused': {
                        borderColor: '#1976d2',
                        backgroundColor: '#e3f2fd',
                      },
                    }}
                    disabled={readOnly}
                    onFocus={handleFocus}
                  />
                </TableCell>
                {visibleColumns.priceWithVat && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(item.price * (1 + (Number(item.vatRate) || 0) / 100))}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <TextField
                    value={formatNumber(totals.netValue)}
                    type="text"
                    size="small"
                    variant="standard"
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true,
                      inputProps: {
                        style: { textAlign: 'center' }
                      }
                    }}
                    sx={{
                      width: AMOUNT_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      }
                    }}
                  />
                </TableCell>
                {visibleColumns.discountPerc && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(item.discount)}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue === '' || !isNaN(parseFloat(newValue))) {
                          onItemChange(item.id, 'discount', parseFloat(newValue) || 0);
                        }
                      }}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{ 
                        disableUnderline: true,
                        inputProps: { 
                          min: 0, 
                          max: 100,
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{ 
                        width: NUMERICAL_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        },
                        '& .MuiInputBase-root': {
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: '4px',
                          paddingX: '4px',
                          transition: 'border-color 0.2s ease',
                        },
                        '& .MuiInputBase-root:hover': {
                          borderColor: '#1976d2',
                        },
                        '& .MuiInputBase-root.Mui-focused': {
                          borderColor: '#1976d2',
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                      disabled={readOnly}
                      onFocus={handleFocus}
                    />
                  </TableCell>
                )}
                {visibleColumns.discountAmt && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(totals.discountAmount)}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                )}
                {visibleColumns.netValue && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(totals.netValueAfterDiscount)}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <TextField
                    value={formatNumber(item.vatRate)}
                    type="text"
                    size="small"
                    variant="standard"
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true,
                      inputProps: {
                        style: { textAlign: 'center' }
                      }
                    }}
                    sx={{
                      width: NUMERICAL_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      }
                    }}
                  />
                </TableCell>
                {visibleColumns.vatAmount && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(totals.vatAmount)}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                )}
                {visibleColumns.grandTotal && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(totals.total)}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                )}
                {visibleColumns.description && (
                  <TableCell align="center">
                    <TextField
                      value={item.description}
                      onChange={(e) => onItemChange(item.id, 'description', e.target.value)}
                      size="small"
                      variant="standard"
                      fullWidth
                      InputProps={{ disableUnderline: true }}
                      sx={{
                        '& .MuiInputBase-root': {
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: '4px',
                          padding: '0 4px',
                          transition: 'border-color 0.2s ease',
                        },
                        '& .MuiInputBase-root:hover': {
                          borderColor: '#1976d2',
                        },
                        '& .MuiInputBase-root.Mui-focused': {
                          borderColor: '#1976d2',
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                      disabled={readOnly}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <IconButton 
                    size="small" 
                    onClick={() => onRemoveItem(item.id)} 
                    sx={{ p: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          
          {/* Totals Row */}
          <TableRow sx={{ 
            position: 'sticky', 
            bottom: 0, 
            bgcolor: '#f1f4fd',
            borderTop: '2px solid rgba(0, 0, 0, 0.12)',
            '& td': {
              fontWeight: 'bold',
              fontSize: '0.875rem',
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            }
          }}>
            <TableCell align="left">Line Totals</TableCell>
            <TableCell align="center">
              {formatNumber(totals.quantity)}
            </TableCell>
            <TableCell align="center">
              {items.length > 0 ? formatNumber(items.reduce((sum, item) => sum + (Number(item.price) || 0), 0) / items.length) : '0.00'}
            </TableCell>
            {visibleColumns.priceWithVat && (
              <TableCell align="center">
                {items.length > 0 ? formatNumber(items.reduce((sum, item) => sum + ((Number(item.price) || 0) * (1 + (Number(item.vatRate) || 0) / 100)), 0) / items.length) : '0.00'}
              </TableCell>
            )}
            <TableCell align="center">
              {formatNumber(totals.lineTotal)}
            </TableCell>
            {visibleColumns.discountPerc && (
              <TableCell align="center">
                {formatNumber((items.reduce((sum, item) => sum + (Number(item.discount) || 0), 0) / 
                  Math.max(items.length, 1)))}
              </TableCell>
            )}
            {visibleColumns.discountAmt && (
              <TableCell align="center">
                {formatNumber(totals.discountAmount)}
              </TableCell>
            )}
            {visibleColumns.netValue && (
              <TableCell align="center">
                {formatNumber(totals.netValue)}
              </TableCell>
            )}
            <TableCell align="center">
              {formatNumber((items.reduce((sum, item) => sum + (Number(item.vatRate) || 0), 0) / 
                Math.max(items.length, 1)))}
            </TableCell>
            {visibleColumns.vatAmount && (
              <TableCell align="center">
                {formatNumber(totals.vatAmount)}
              </TableCell>
            )}
            {visibleColumns.grandTotal && (
              <TableCell align="center">
                {formatNumber(totals.grandTotal)}
              </TableCell>
            )}
            {visibleColumns.description && <TableCell />}
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>

    {/* Column Settings Popover */}
    <Popover
      open={settingsOpen}
      anchorEl={settingsAnchorEl}
      onClose={handleSettingsClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <List>
        {Object.entries(visibleColumns).map(([column, visible]) => (
          <ListItem key={column} button onClick={() => handleColumnToggle(column)}>
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={visible}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, ' $1')} />
          </ListItem>
        ))}
      </List>
    </Popover>

    {/* Error Message Snackbar */}
    <Snackbar
      open={showError}
      autoHideDuration={4000}
      onClose={() => setShowError(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setShowError(false)}
        severity="error"
        variant="filled"
        sx={{ width: '100%' }}
      >
        {errorMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default CreditNoteItemsTable;
